/**
 * Turning a manifest's `label=` key into text the palette and inspector can show.
 *
 * Component labels do **not** live in the backoffice locale files: the `.twig`
 * components declare `label=email_component_labels.button.text`, and the backend
 * resolves those keys from the global `translations` catalog for the request
 * locale (`GET /email-component-labels`). This module is the last mile — map
 * lookup plus the fallbacks that keep a control from rendering nameless.
 *
 * Fallback order for a param:
 *   1. the resolved catalog text for its `labelKey`
 *   2. a literal `labelKey` that isn't a key at all (partner fragments may
 *      declare display text; `core/*` may not — the manifest parser warns)
 *   3. a humanized form of the param name (`bg_color` → "Bg color")
 */
import {
  FriendlyLabel,
  fragmentDisplayName,
  humanizeVariablePath,
} from '../labels';
import { ManifestParam, ManifestSlot } from './manifest';
import { BlockDefinition } from './registry';

/** `group.key` shape — dots and no whitespace. Display text has neither. */
function looksLikeKey(value: string): boolean {
  return /^[a-z][a-z0-9_]*(\.[a-z0-9_]+)+$/i.test(value);
}

/** `bg_color` → "Bg color"; `full_width` → "Full width". */
export function humanizeParamName(name: string): string {
  const words = name.replace(/[_-]+/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1).toLowerCase();
}

/** The last segment of a key, humanized: `…columns.col1` → "Col1". */
function humanizeKeyTail(key: string): string {
  const segments = key.split('.').filter(Boolean);
  return humanizeParamName(segments[segments.length - 1] ?? key);
}

export function resolveLabel(
  labelKey: string | null | undefined,
  labels: Record<string, string> = {},
  fallback = '',
): string {
  if (!labelKey) return fallback;

  const resolved = labels[labelKey];
  if (resolved) return resolved;

  // A partner fragment may legitimately declare display text instead of a key.
  if (!looksLikeKey(labelKey)) return labelKey;

  // The key exists but the catalog has no value — better a humanized name than
  // a raw `email_component_labels.…` string in the UI.
  return fallback || humanizeKeyTail(labelKey);
}

export function resolveParamLabel(
  param: ManifestParam,
  labels?: Record<string, string>,
): string {
  return resolveLabel(param.labelKey, labels, humanizeParamName(param.name));
}

export function resolveSlotLabel(
  slot: ManifestSlot,
  labels?: Record<string, string>,
): string {
  return resolveLabel(slot.labelKey, labels, humanizeParamName(slot.name));
}

/**
 * The palette title for a block type. Falls back to the component's own name
 * (`core.button` → "Button", `fragment:partials/xxxl/footer` → "Footer") so an
 * undeclared or untranslated component is still recognizable.
 */
export function resolveBlockLabel(
  definition: BlockDefinition,
  labels?: Record<string, string>,
): string {
  const derived = humanizeParamName(
    (definition.fragmentKey ?? definition.type)
      .replace(/^fragment:/, '')
      .replace(/@\d+$/, '')
      .split(/[/.]/)
      .filter(Boolean)
      .pop() ?? definition.type,
  );

  return resolveLabel(definition.labelKey, labels, derived);
}

/** Types owned by the component library we ship. */
const CORE_TYPE_PREFIX = 'core.';

/**
 * What every surface calls a block: the palette, the structure tree and the
 * selection readout.
 *
 * The two families are named differently on purpose:
 *
 * - **Core components** carry a declared, translated label. There is exactly one of
 *   each, and its name is a translation key rather than a path.
 * - **Partner fragments** are named by their own `name`, and by their *path* when
 *   they have none — a partner catalog holds `partials/header`,
 *   `partials/xxxl/header` and more, all of which humanize to "Header" on their
 *   own, so `fragmentDisplayName` gives "Xxxl · Header".
 *
 * Centralised because having the palette say one thing and the tree another for the
 * same block is its own bug — observed before this existed.
 */
export function resolveBlockDisplayLabel(
  definition: BlockDefinition,
  labels?: Record<string, string>,
): string {
  if (definition.fragmentKey && !definition.type.startsWith(CORE_TYPE_PREFIX)) {
    return fragmentDisplayName({
      key: definition.fragmentKey,
      name: definition.fragmentName,
    });
  }
  return resolveBlockLabel(definition, labels);
}

/**
 * What to call a variable in the fragment editor's sample-data rows.
 *
 * A block's own fields carry an "Editors see" name — that is the whole point of
 * declaring one — so the preview panels have to use it rather than humanizing
 * the identifier behind it. Filling in a row called "Btn txt 2" when the block
 * calls the same thing "Button label" is the panel disagreeing with itself.
 *
 * Falls through to the path form for everything a block does not declare:
 * `theme.colors.brand`, `email_config.template_vars.logo` and the rest are
 * send-time context, not fields, and their namespace is the useful part.
 */
export function resolveVariableLabel(
  path: string,
  params: readonly ManifestParam[] = [],
  labels?: Record<string, string>,
): FriendlyLabel {
  const declared = params.find((param) => param.name === path);
  if (!declared?.labelKey) return humanizeVariablePath(path);
  return {
    label: resolveLabel(
      declared.labelKey,
      labels,
      humanizeVariablePath(path).label,
    ),
    // A field is a bare identifier: there is no namespace to show beside it.
    context: '',
  };
}
